import { FC, ReactNode, useContext, useState } from 'react';
import { Page } from './AppointmentGrid.styles';
import { useTranslation } from 'react-i18next';
import {
  LuxGrid,
  Stack,
  Typography,
  Box,
  LuxSelectablePill,
  LuxButton,
} from '@gemini/fe-ds-core';
import { useGetAppointments } from '@hooks/useGetAppointments';
import { DateUtils } from '@gemini/lux-ds-pms';
import AppointmentsContext from '@context/AppointmentsContext';
import OrganizationLocationContext from '@context/OrganizationLocationContext';
import { getAppointmentStatusColor } from '@utils/LuxColorUtil';
import ImportDeviceDataContext from '@context/ImportDeviceDataContext';
import GridHeader from '@components/Grid/GridHeader';
import GridCell from '@components/Grid/GridCell';

interface AppointmentGridProps {
  children?: ReactNode;
}

const AppointmentGrid: FC<AppointmentGridProps> = () => {
  const [page, setPage] = useState(0);
  const { t } = useTranslation();
  const { date } = useContext(AppointmentsContext);
  const { organizationCoreId, locationCoreId, isSwitchingOrganization } =
    useContext(OrganizationLocationContext);
  const { setAppointment, setModalOpen } = useContext(ImportDeviceDataContext);
  const {
    data: appointments,
    isLoading,
    error,
  } = useGetAppointments({
    enabled: !isSwitchingOrganization,
    organizationCoreId: organizationCoreId,
    locationCoreId: locationCoreId,
    date: date,
  });

  return (
    <Page>
      <Stack flexDirection={'column'} width={'100%'} gap={'1.5rem'}>
        <Typography variant="heading4">
          {t('examImport.table.title')}
        </Typography>
        <Box>
          {!error ? (
            <LuxGrid
              id="exam-import-grid"
              rowStyle={{ minHeight: '4rem' }}
              cellStyle={{ display: 'flex', alignItems: 'center' }}
              rowHeight={69}
              tableHeight={401}
              headerHeight={53}
              loading={date != null && isLoading}
              luxPagination={{
                sizeOptions: [5],
                current: page,
                totalElements: appointments?.items?.length ?? 0,
                selectedSize: 5,
                onSelectPage: p => {
                  setPage(p);
                },
                onSizeChange: () => {},
              }}
              columnDefs={[
                {
                  field: 'patientName',
                  flex: 1,
                  cellRenderer: (params: any) => {
                    return (
                      <GridCell>
                        <Typography variant="textMdRegular">
                          {params.data.patientName}
                        </Typography>
                        <Typography variant="textXs" color="text.secondary">
                          {DateUtils.format(params.data.patientBirthDate)}
                        </Typography>
                      </GridCell>
                    );
                  },
                  headerComponent: () => (
                    <GridHeader
                      header={t('examImport.table.patientName') ?? ''}
                    />
                  ),
                },
                {
                  field: 'date',
                  flex: 1,
                  cellRenderer: (params: any) => {
                    return (
                      <GridCell>
                        <Typography variant="textMdRegular">
                          {DateUtils.format(params.data.date)}
                        </Typography>
                        <Typography variant="textXs" color="text.secondary">
                          {DateUtils.format(params.data.date, 'hh:mm A')}
                        </Typography>
                      </GridCell>
                    );
                  },
                  headerComponent: () => (
                    <GridHeader
                      header={t('examImport.table.appointmentDate') ?? ''}
                    />
                  ),
                },
                {
                  field: 'providerName',
                  cellRenderer: (params: any) => {
                    return (
                      <GridCell>
                        <Typography variant="textMdRegular">
                          {params.data.providerName}
                        </Typography>
                      </GridCell>
                    );
                  },
                  headerComponent: () => (
                    <GridHeader
                      header={t('examImport.table.providerName') ?? ''}
                    />
                  ),
                  flex: 1,
                },
                {
                  field: 'examType',
                  cellRenderer: (params: any) => {
                    return (
                      <GridCell>
                        <LuxSelectablePill
                          selectable={false}
                          color="info"
                          text={params.data.examType}
                        />
                      </GridCell>
                    );
                  },
                  headerComponent: () => (
                    <GridHeader header={t('examImport.table.examType') ?? ''} />
                  ),
                  flex: 1,
                },
                {
                  field: 'examStatus',
                  cellRenderer: (params: any) => {
                    return (
                      <GridCell>
                        <LuxSelectablePill
                          selectable={false}
                          color={getAppointmentStatusColor(
                            params.data.examStatus
                          )}
                          text={params.data.examStatus}
                        />
                      </GridCell>
                    );
                  },
                  flex: 1,
                  headerComponent: () => (
                    <GridHeader
                      header={t('examImport.table.examStatus') ?? ''}
                    />
                  ),
                },
                {
                  field: 'id',
                  headerName: t('examImport.table.action') ?? '',
                  flex: 1,
                  cellRenderer: (params: any) => {
                    return (
                      <GridCell>
                        <LuxButton
                          variant="secondary"
                          iconEnd="ChevronRightIcon"
                          onClick={() => {
                            setAppointment(params.data);
                            setModalOpen(true);
                          }}
                        >
                          {t('examImport.table.openDataImport')}
                        </LuxButton>
                      </GridCell>
                    );
                  },
                  headerComponent: () => (
                    <GridHeader header={t('examImport.table.examType') ?? ''} />
                  ),
                },
              ]}
              rowData={appointments?.items?.slice(page * 5, page * 5 + 5) ?? []}
            />
          ) : (
            <Typography variant="textMdRegular">
              {t('examImport.table.loadError')}
            </Typography>
          )}
        </Box>
      </Stack>
    </Page>
  );
};

export default AppointmentGrid;
